import React from 'react'
import isUndefined from 'lodash/isUndefined'
import { AccountNumber, Address } from 'app-types'

import { CodeText } from '../redux/reducers/codetable/model'

export const flatten = (original: any, root: any): Object[] =>
  Object.keys(original).map((key: any) =>
    original[key] && typeof original[key] === 'object' && !React.isValidElement(original[key])
      ? flatten(original[key], [root, key].filter(x => x).join('.'))
      : {
          key: [root, key].filter(x => x).join('.'),
          value: original[key],
        }
  ) || []

export const getSubmitErrorNames = (errors: object): string[] =>
  flatten(errors, null)
    .filter((item: any) => item.value)
    .map((item: any) => item.key)

export const composeFormFields = (fields: object): object => ({
  form: mutateFields(fields),
})

const mutateFields = (fields: any) =>
  fields &&
  Object.keys(fields).reduce((obj, key) => {
    const newObj: any = obj

    if (typeof fields[key] === 'object') {
      newObj[key] = mutateFields(fields[key])
    } else {
      newObj[key] = {
        // Encapsulate value for form fields
        value: fields[key] || null,
        name: key,
      }
    }

    return newObj
  }, {})

export const hasObjectSomeValue = (fields: any): boolean =>
  getLeafsValuesBooleans(fields).some((item: any) => Boolean(item))

const getLeafsValuesBooleans = (obj: any): any[] =>
  Object.keys(obj).map(key =>
    obj[key] && typeof obj[key] === 'object' ? getLeafsValuesBooleans(obj[key]) : Boolean(obj[key])
  ) || []

export const getUniqueId = () => `${new Date().getTime().toString()}_${Math.random()}`

// If it is just a 5-digit string, pretty-print it with a space separator
export const preformatZip = (zip: string) =>
  /^\d{5}$/.test(zip) ? `${zip.slice(0, 3)} ${zip.slice(3, 5)}` : zip

// If it is a string of digits without a slash, insert one after the date portion
export const preformatBirthNumber = (bn: string) =>
  /^\d+$/.test(bn) ? `${bn.slice(0, 6)}/${bn.slice(6)}` : bn

export const codetableContains = (table: CodeText[], code: string) =>
  Array.isArray(table) && table.find((pair: CodeText) => code === pair.code)

export const steps = (from: number, toIncl: number, step: number) => {
  const out: number[] = []
  let n: number = from
  while (n < toIncl) {
    out.push(n)
    n += step
  }
  out.push(toIncl)
  return out
}

/** add trailing zeros when number is shorter then 4 characters */
export const addTrailingZeros = (bankCode: number | string = ''): string =>
  `${bankCode}`.length < 4 ? addTrailingZeros(`0${bankCode}`) : `${bankCode}`

export const formatAccountNumber = (accountNumber?: AccountNumber): string => {
  if (accountNumber && accountNumber.number) {
    const { prefix, number, bankCode } = accountNumber
    return [prefix && prefix > 0 ? `${prefix}-${number}` : number, addTrailingZeros(bankCode)].join(
      '/'
    )
  }
  return ''
}

export const formatAddress = ({
  city,
  houseNumber = '',
  landRegistryNumber,
  street,
  zip,
}: Address): string =>
  [
    `${street} ${landRegistryNumber}${houseNumber === '' ? '' : `/${houseNumber}`}`,
    city,
    [zip.slice(0, 3), zip.slice(3)].join(' '),
  ].join(', ')

export const trim = (value: string | number | undefined = '') => `${value}`.trim()

interface Options {
  maximumFractionDigits?: number
  minimumFractionDigits?: number
  minimumIntegerDigits?: number
}

export const clamp = (min: number, max: number, value: number): number =>
  Math.min(Math.max(value, min), max)

export const round = (offset: number, multiple: number, value: number): number =>
  Math.round((value - offset) / multiple) * multiple + offset

export const getPercentageFromValue = (
  min: number,
  max: number,
  step: number,
  value: number
): number => clamp(0, 1, (round(min, step, value) - min) / (max - min))

export const NON_BREAKING_SPACE = '\u00a0'

export const numberToPercent = (value?: number, options?: Options): string =>
  !isUndefined(value) && value !== null
    ? `${numberToFormat(value, options)}${NON_BREAKING_SPACE}%`
    : ''

export const numberToFormat = (amount?: number, options?: Options): string =>
  !isUndefined(amount) && amount != null
    ? amount.toLocaleString('cs', {
        style: 'decimal',
        ...parseOptions(options),
      })
    : ''

export const parseOptions = (options?: Options): Object => ({
  minimumFractionDigits: (options && options.minimumFractionDigits) || 0,
  maximumFractionDigits: (options && options.maximumFractionDigits) || 2,
  minimumIntegerDigits: (options && options.minimumIntegerDigits) || 1,
})
